import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaStream } from '@react-icons/all-files/fa/FaStream';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { DeleteButton, RenderActions, WorkflowInstanciaNodoButton } from '../../../../commons/DataGridActions';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/workflow-instancia.reducer';
import UserInfo from '../../../../commons/UserInfo';
import LinkCell from '../../../../commons/LinkCell';

// ── Estado badge ──────────────────────────────────────────────────────────────

const ESTADO_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  GENERADO: { label: 'Generado', bg: '#eff6ff', color: '#1d4ed8' },
  EJECUCION: { label: 'En Ejecución', bg: '#f0fdf4', color: '#15803d' },
  FINALIZADO: { label: 'Finalizado', bg: '#f1f5f9', color: '#0f172a' },
};

function EstadoBadge({ estado }: { estado?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const cfg = ESTADO_CONFIG[estado.toUpperCase()] ?? { label: estado, bg: '#f1f5f9', color: '#475569' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      background: cfg.bg,
      color: cfg.color,
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const BASE = '/configuracion/workflow/instancias-workflow';

export default function InstanciasWorkflowPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => (s as any).workflowInstancia.entities);
  const totalItems = useAppSelector(s => (s as any).workflowInstancia.totalItems);
  const loading = useAppSelector(s => (s as any).workflowInstancia.loading);
  const updateSuccess = useAppSelector(s => (s as any).workflowInstancia.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();
  const lastParamsRef = useRef<any>(null);

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      showToast('success', 'Instancia eliminada correctamente.');
      if (lastParamsRef.current) dispatch(getEntities(lastParamsRef.current));
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = (params: any) => {
    lastParamsRef.current = params;
    dispatch(getEntities(params));
  };

  const handleDelete = (row: any) => {
    ask(
      `¿Confirmas la eliminación de la instancia #${row.id}?`,
      async () => {
        setConfirmLoading(true);
        await dispatch(deleteEntity(row.id));
        setConfirmLoading(false);
        dismiss();
      },
      { title: 'Eliminar instancia', variant: 'danger' },
    );
  };

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },

    {
      field: 'workflowEmpresa.workflow.nombre',
      headerName: 'Workflow',
      flex: 2,
      renderCell: (params: any) => (
        <LinkCell
          label={params.row.workflowEmpresa?.workflow?.nombre ?? ' - '}
          filterName="workflow_id"
          filterValue={params.row.profaseWorkflow?.id ?? params.row.workflow?.id}
        />
      ),
    },
    {
      field: 'workflowEmpresa.empresa.rfc',
      headerName: 'RFC',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa.empresa;
        return emp ? `${emp.rfc ?? emp.nombre ?? ''}` : '—';
      },
    },
    {
      field: 'workflowEmpresa.empresa.razonSocial',
      headerName: 'Razón Social',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa.empresa;
        return emp ? `${emp.razonSocial ?? emp.nombre ?? ''}` : '—';
      },
    },
    {
      field: 'workflowEmpresa.empresa.grupoEmpresarial.alias',
      headerName: 'Grupo',
      flex: 2,
      renderCell: (params: any) => {
        const emp = params.row.workflowEmpresa.empresa;
        return emp ? `${emp.grupoEmpresarial?.alias ?? emp.nombre ?? ''}` : '—';
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 1.2,
      renderCell: (params: any) => <EstadoBadge estado={params.row.estado} />,
    },
    {
      field: '__actions',
      headerName: '',
      flex: 0.5,
      renderCell: (params: any) => (
        <RenderActions>
          <WorkflowInstanciaNodoButton onClick={() => navigate(`/configuracion/workflow/instancias-nodos?workflowNodo.tipoNodo=actividad&workflowInstancia.id=${params.row.id}`)}></WorkflowInstanciaNodoButton>

          <DeleteButton onClick={() => handleDelete(params.row)} />
        </RenderActions>
      ),
    },
  ];

  const filters = [
    { label: 'Workflow ID', name: 'workflow_id' },
    { label: 'Workflow Nombre', name: 'workflowEmpresa.workflow.nombre' },
    { label: 'RFC', name: 'workflowEmpresa.empresa.rfc' },
    { label: 'Razón Social', name: 'workflowEmpresa.empresa.razonSocial' },
    { label: 'Grupo', name: 'workflowEmpresa.empresa.grupoEmpresarial.alias' },
    { label: 'Empresa ID', name: 'empresa_id' },
    {
      label: 'Estado',
      name: 'estado',
      type: 'select',
      options: [
        { value: 'NUEVO', label: 'Nuevo' },
        { value: 'GENERADO', label: 'Generado' },
        { value: 'EJECUCION', label: 'En Ejecución' },
        { value: 'FINALIZADO', label: 'Finalizado' },
      ],
    },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Instancias de Workflow', icon: <FaStream /> },
      ]} />

      <PageHeader
        icon={<FaStream />}
        iconColor="#f59e0b"
        title="Instancias de Workflow"
        subtitle="Consulta y eliminación de instancias generadas"
        action={{ label: 'Nueva Instancia', onClick: () => navigate(`${BASE}/new`) }}
      />

      <Confirm config={confirmConfig} />

      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />


    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
